// [CONTENT CREATORS - FEB 2026]: Volo's Guide to Monsters Sourcebook
// [PURPOSE]: Training example for the Race Creator system — Aasimar race with 3 subraces
// [D&D 5E VGTM]: Volo's Guide to Monsters (2016) — Race options chapter
export const VOLOS_SOURCE = {
  id: 'volos',
  name: "Volo's Guide to Monsters",
  author: 'Wizards of the Coast',
  version: '1.0',
  description: 'A supplement that provides player race options including Aasimar, Firbolg, Goliath, Kenku, Lizardfolk, Tabaxi, and Triton.',
  enabled: true,
  removable: true,
  builtin: true, // [CW-TRIO-SOURCES 2026-07-12]: shipped sourcebook — excluded from user-content listings
  categories: {
    items: false,
    spells: false,
    feats: false,
    backgrounds: false,
    subclasses: false,
    races: true,
    classes: false,
    abilities: false,
    boons: false
  },
  content: {
    items: {},
    spells: {},
    feats: {},
    backgrounds: {},
    subclasses: {},
    // [CONTENT CREATORS - FEB 2026]: Aasimar race — training example for Race Creator
    // [D&D 5E VGTM p.104]: Celestial-touched humanoids with divine heritage
    races: {
      aasimar: {
        id: 'aasimar',
        name: 'Aasimar',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { cha: 2 },
        size: 'Medium',
        speed: 30,
        features: ['Darkvision', 'Celestial Resistance', 'Healing Hands', 'Light Bearer'],
        featureDescriptions: {
          'Darkvision': 'Blessed with a radiant soul, your vision can easily cut through darkness. You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light.',
          'Celestial Resistance': 'You have resistance to necrotic damage and radiant damage.',
          'Healing Hands': 'As an action, you can touch a creature and cause it to regain a number of hit points equal to your level. Once you use this trait, you can\'t use it again until you finish a long rest.',
          'Light Bearer': 'You know the Light cantrip. Charisma is your spellcasting ability for it.'
        },
        proficiencies: {
          weapons: [],
          tools: [],
          armor: [],
          skills: []
        },
        languages: ['Common', 'Celestial'],
        darkvision: 60,
        damageResistance: ['necrotic', 'radiant'],
        savingThrows: {},
        hitPointBonus: 0,
        grantedSpells: [
          { name: 'Light', level: 0, source: 'race' }
        ],
        effects: [
          { type: 'resistance', operation: 'grant', value: 'necrotic' },
          { type: 'resistance', operation: 'grant', value: 'radiant' }
        ],
        subraces: {
          'Protector Aasimar': {
            abilityScoreIncrease: { wis: 1 },
            features: ['Radiant Soul'],
            featureDescriptions: {
              'Radiant Soul': 'Starting at 3rd level, you can use your action to unleash the divine energy within yourself, causing your eyes to glimmer and two luminous, incorporeal wings to sprout from your back. Your transformation lasts for 1 minute or until you end it as a bonus action. During it, you have a flying speed of 30 feet, and once on each of your turns, you can deal extra radiant damage to one target equal to your level.'
            }
          },
          'Scourge Aasimar': {
            abilityScoreIncrease: { con: 1 },
            features: ['Radiant Consumption'],
            featureDescriptions: {
              'Radiant Consumption': 'Starting at 3rd level, you can use your action to unleash the divine energy within yourself, causing a searing light to radiate from you. Your transformation lasts for 1 minute or until you end it as a bonus action. During it, you shed bright light in a 10-foot radius and dim light for an additional 10 feet, and at the end of each of your turns, you and each creature within 10 feet of you take radiant damage equal to half your level (rounded up). In addition, once on each of your turns, you can deal extra radiant damage to one target equal to your level.'
            }
          },
          'Fallen Aasimar': {
            abilityScoreIncrease: { str: 1 },
            features: ['Necrotic Shroud'],
            featureDescriptions: {
              'Necrotic Shroud': 'Starting at 3rd level, you can use your action to unleash the divine energy within yourself, causing your eyes to turn into pools of darkness and two skeletal, ghostly, flightless wings to sprout from your back. The instant you transform, other creatures within 10 feet of you that can see you must succeed on a Charisma saving throw (DC 8 + your proficiency bonus + your Charisma modifier) or become frightened of you until the end of your next turn. Your transformation lasts for 1 minute or until you end it as a bonus action. During it, once on each of your turns, you can deal extra necrotic damage to one target equal to your level.'
            }
          }
        }
      },

      // [v1.1]: Firbolg — VGTM p.106
      firbolg: {
        id: 'firbolg',
        name: 'Firbolg',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { wis: 2, str: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Firbolg Magic', 'Hidden Step', 'Powerful Build', 'Speech of Beast and Leaf'],
        featureDescriptions: {
          'Firbolg Magic': 'You can cast Detect Magic and Disguise Self with this trait, using Wisdom as your spellcasting ability. Once you cast either spell, you can\'t cast it again until you finish a short or long rest. When you use Disguise Self, you can seem up to 3 feet shorter, letting you more easily blend in with humans and elves.',
          'Hidden Step': 'As a bonus action, you can magically turn invisible until the start of your next turn or until you attack, make a damage roll, or force someone to make a saving throw. Once you use this trait, you can\'t use it again until you finish a short or long rest.',
          'Powerful Build': 'You count as one size larger when determining your carrying capacity and the weight you can push, drag, or lift.',
          'Speech of Beast and Leaf': 'You have the ability to communicate in a limited manner with beasts and plants. They can understand the meaning of your words, though you have no special ability to understand them in return. You have advantage on all Charisma checks you make to influence them.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: [] },
        languages: ['Common', 'Elvish', 'Giant'],
        darkvision: 0,
        damageResistance: [],
        savingThrows: {},
        hitPointBonus: 0,
        grantedSpells: [
          { name: 'Detect Magic', level: 1, source: 'race', recharge: 'short' },
          { name: 'Disguise Self', level: 1, source: 'race', recharge: 'short' }
        ],
        effects: [],
        subraces: {}
      },

      // [v1.1]: Goliath — VGTM p.108
      goliath: {
        id: 'goliath',
        name: 'Goliath',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { str: 2, con: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Natural Athlete', "Stone's Endurance", 'Powerful Build', 'Mountain Born'],
        featureDescriptions: {
          'Natural Athlete': 'You have proficiency in the Athletics skill.',
          "Stone's Endurance": 'You can focus yourself to occasionally shrug off injury. When you take damage, you can use your reaction to roll a d12 + your Constitution modifier. Reduce the damage by that total. After you use this trait, you can\'t use it again until you finish a short or long rest.',
          'Powerful Build': 'You count as one size larger when determining your carrying capacity and the weight you can push, drag, or lift.',
          'Mountain Born': 'You have resistance to cold damage. You\'re also acclimated to high altitude, including elevations above 20,000 feet.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: ['Athletics'] },
        languages: ['Common', 'Giant'],
        darkvision: 0,
        damageResistance: ['cold'],
        savingThrows: {},
        hitPointBonus: 0,
        grantedSpells: [],
        effects: [
          { type: 'resistance', operation: 'grant', value: 'cold' },
          { type: 'skill', target: 'athletics', operation: 'grant', value: 'proficiency' }
        ],
        subraces: {}
      },

      // [v1.1]: Kenku — VGTM p.109
      kenku: {
        id: 'kenku',
        name: 'Kenku',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { dex: 2, wis: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Expert Forgery', 'Kenku Training', 'Mimicry'],
        featureDescriptions: {
          'Expert Forgery': 'You can duplicate other creatures\' handwriting and craftwork. You have advantage on all checks made to produce forgeries or duplicates of existing objects.',
          'Kenku Training': 'You are proficient in your choice of two of the following skills: Acrobatics, Deception, Stealth, and Sleight of Hand.',
          'Mimicry': 'You can mimic sounds you have heard, including voices. A creature that hears the sounds you make can tell they are imitations with a successful Wisdom (Insight) check opposed by your Charisma (Deception) check.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: [] },
        languages: ['Common', 'Auran'],
        darkvision: 0,
        damageResistance: [],
        savingThrows: {},
        hitPointBonus: 0,
        grantedSpells: [],
        effects: [],
        subraces: {}
      },

      // [v1.1]: Lizardfolk — VGTM p.111
      lizardfolk: {
        id: 'lizardfolk',
        name: 'Lizardfolk',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { con: 2, wis: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Bite', 'Cunning Artisan', 'Hold Breath', "Hunter's Lore", 'Natural Armor', 'Hungry Jaws'],
        featureDescriptions: {
          'Bite': 'Your fanged maw is a natural weapon, which you can use to make unarmed strikes. If you hit with it, you deal 1d6 + your Strength modifier piercing damage, instead of the bludgeoning damage normal for an unarmed strike.',
          'Cunning Artisan': 'As part of a short rest, you can harvest bone and hide from a slain beast, construct, dragon, monstrosity, or plant creature of size Small or larger to create one of the following: a shield, a club, a javelin, or 1d4 darts or blowgun needles.',
          'Hold Breath': 'You can hold your breath for up to 15 minutes at a time.',
          "Hunter's Lore": 'You gain proficiency with two of the following skills of your choice: Animal Handling, Nature, Perception, Stealth, and Survival.',
          'Natural Armor': 'You have tough, scaly skin. When you aren\'t wearing armor, your AC is 13 + your Dexterity modifier. You can use your natural armor to determine your AC if the armor you wear would leave you with a lower AC. A shield\'s benefits apply as normal while you use your natural armor.',
          'Hungry Jaws': 'In battle, you can throw yourself into a vicious feeding frenzy. As a bonus action, you can make a special attack with your bite. If the attack hits, it deals its normal damage, and you gain temporary hit points equal to your Constitution modifier (minimum of 1). After you use this trait, you can\'t use it again until you finish a short or long rest.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: [] },
        languages: ['Common', 'Draconic'],
        darkvision: 0,
        damageResistance: [],
        savingThrows: {},
        hitPointBonus: 0,
        swimSpeed: 30,
        grantedSpells: [],
        effects: [
          { type: 'ac', target: 'natural', operation: 'set', value: 13, condition: 'unarmored' }
        ],
        subraces: {}
      },

      // [v1.1]: Tabaxi — VGTM p.113
      tabaxi: {
        id: 'tabaxi',
        name: 'Tabaxi',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { dex: 2, cha: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Darkvision', 'Feline Agility', "Cat's Claws", "Cat's Talent"],
        featureDescriptions: {
          'Darkvision': 'You have a cat\'s keen senses, especially in the dark. You can see in dim light within 60 feet of you as if it were bright light, and in darkness as if it were dim light. You can\'t discern color in darkness, only shades of gray.',
          'Feline Agility': 'Your reflexes and agility allow you to move with a burst of speed. When you move on your turn in combat, you can double your speed until the end of the turn. Once you use this trait, you can\'t use it again until you move 0 feet on one of your turns.',
          "Cat's Claws": 'Because of your claws, you have a climbing speed of 20 feet. In addition, your claws are natural weapons, which you can use to make unarmed strikes. If you hit with them, you deal 1d4 + your Strength modifier slashing damage.',
          "Cat's Talent": 'You have proficiency in the Perception and Stealth skills.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: ['Perception', 'Stealth'] },
        languages: ['Common'],
        darkvision: 60,
        damageResistance: [],
        savingThrows: {},
        hitPointBonus: 0,
        climbSpeed: 20,
        // [S4]: Data-driven movement trait — allows SensesMovementPanel to detect
        // this ability via property instead of race name string check
        felineAgility: true,
        grantedSpells: [],
        effects: [
          { type: 'skill', target: 'perception', operation: 'grant', value: 'proficiency' },
          { type: 'skill', target: 'stealth', operation: 'grant', value: 'proficiency' }
        ],
        subraces: {}
      },

      // [v1.1]: Triton — VGTM p.115
      triton: {
        id: 'triton',
        name: 'Triton',
        source: 'volos',
        isCustom: false,
        abilityScoreIncrease: { str: 1, con: 1, cha: 1 },
        size: 'Medium',
        speed: 30,
        features: ['Amphibious', 'Control Air and Water', 'Emissary of the Sea', 'Guardians of the Depths'],
        featureDescriptions: {
          'Amphibious': 'You can breathe air and water.',
          'Control Air and Water': 'A child of the sea, you can call on the magic of elemental air and water. You can cast Fog Cloud with this trait. Starting at 3rd level, you can cast Gust of Wind. Starting at 5th level, you can cast Wall of Water. Once you cast a spell with this trait, you can\'t cast it again until you finish a long rest. Charisma is your spellcasting ability for these spells.',
          'Emissary of the Sea': 'Aquatic beasts have an extraordinary affinity with your people. You can communicate simple ideas with beasts that can breathe water. They can understand the meaning of your words, though you have no special ability to understand them in return.',
          'Guardians of the Depths': 'Adapted to even the most extreme ocean depths, you have resistance to cold damage.'
        },
        proficiencies: { weapons: [], tools: [], armor: [], skills: [] },
        languages: ['Common', 'Primordial'],
        darkvision: 60,
        damageResistance: ['cold'],
        savingThrows: {},
        hitPointBonus: 0,
        swimSpeed: 30,
        grantedSpells: [
          { name: 'Fog Cloud', level: 1, source: 'race', recharge: 'long' },
          { name: 'Gust of Wind', level: 2, source: 'race', recharge: 'long', minLevel: 3 },
          { name: 'Wall of Water', level: 3, source: 'race', recharge: 'long', minLevel: 5 }
        ],
        effects: [
          { type: 'resistance', operation: 'grant', value: 'cold' }
        ],
        subraces: {}
      }
    },
    classes: {},
    abilities: {},
    boons: {}
  }
};
